import { axios, exportAction } from '@/libs/api.request';
import { Response } from '@/types/request.d';
import i18n from '@/locales';

export interface CreatePlanParams {
    sys_email_id?: string;
    name: string;
    label_ids: string;
    email_title: string;
    email_content: string;
    template_content: string;
    template_id: string;
    begin_config: '0' | '1';
    begin_time?: string;
    timezone?: string;
    sender_name: string;
    email: string;
    is_draft: '0' | '1'; // 是否是草稿 （0 不是，1 是）
    module?: string;
    variable_config?: string;
    attachments: string;
}
/**
 * 创建邮箱发送计划
 * @param params
 * @returns
 */
export const createPlan = (params: CreatePlanParams) => axios.request({
    url: '/email/email-marketing/create-plan',
    method: 'post',
    data: params,
});
interface PlanListParams {
    name: string;
    page: string;
    page_size: string;
    /** 发送通道：0 全部 1 SaleSmartly 2 SendCloud */
    send_account_type: string;
}
export interface PlanItem {
    id: string;
    name: string;
    /** 发送通道：1 SaleSmartly 2 SendCloud */
    send_account_type: string;
    status: '0' | '1' | '2';
    all_count: string;
    success_count: string;
    fail_count: string;
    created_time: string;
    success_rate: number;
    fail_rate: number;
}
interface PlanListRes {
    list: PlanItem[];
}
/**
 * 获取发送邮箱计划列表
 * @param params
 * @returns
 */
export const getPlanList = (params: PlanListParams) => axios.request<Response<PlanListRes>>({
    url: '/email/email-marketing/get-plan-list',
    method: 'get',
    params,
    // mock: true,
});
interface PlanCountParams {
    name: string;
    /** 发送通道：0 全部 1 SaleSmartly 2 SendCloud */
    send_account_type: string;
}
interface PlanCountRes {
    total: string;
}
/**
 * 获取发送邮箱计划列表总数
 * @param params
 * @returns
 */
export const getPlanListCount = (params: PlanCountParams) => axios.request<Response<PlanCountRes>>({
    url: '/email/email-marketing/get-plan-count',
    method: 'get',
    params,
    // mock: true,
});
interface PlanDetailParams {
    id: string;
}

export interface PlanDetailQuery {
    ids: string;
    exclude_chat_user_ids: string;
    type: string;
    user_type: string;
    query: string;
    label_ids: string;
    advanced_query_type: string;
    advanced_query: string;
    created_time?: string;
    user_ids?: string;
    channel?: string;
    country?: string;
    language?: string;
    translate_language?: string;
    is_online?: string;
    is_black?: string;
    group_type?: string;
    module?: string;
}

interface PlanDetailRes {
    sys_email_id: string;
    name: string;
    sender_name: string;
    email: string;
    label_ids: string;
    email_title: string;
    email_content: string;
    begin_config: '0' | '1';
    begin_time: number;
    timezone: string;
    template_id: string;
    template_content: string;
    query: PlanDetailQuery;
    module: string,
    attachments: string;
    audience: string;
    variable_config: string;
}
/**
 * 获取发送邮箱计划详情
 * @param params
 * @returns
 */
export const getPlan = (params: PlanDetailParams) => axios.request<Response<PlanDetailRes>>({
    url: '/email/email-marketing/get-plan',
    method: 'get',
    params,
});
export interface UpdatePlanParams extends CreatePlanParams{
    id: string;
}
/**
 * 编辑邮箱发送计划
 * @param params
 * @returns
 */
export const updatePlan = (params: UpdatePlanParams) => axios.request({
    url: '/email/email-marketing/update-plan',
    method: 'post',
    data: params,
    customTips: true,
});
interface DeletePlanParams {
    ids: string;
}
/**
 * 删除邮箱发送计划
 * @param params
 * @returns
 */
export const deletePlan = (params: DeletePlanParams) => axios.request({
    url: '/email/email-marketing/del-plan-list',
    method: 'post',
    data: params,
});
interface PlanInfoParams {
    id: string;
}
interface PlanInfoRes {
    plan_info: {
        name: string;
        email: string;
        sender_name: string;
        email_title: string;
        email_content: string;
        timezone: string;
        begin_time: string;
        begin_config: string;
        audience: string;
        created_time: string;
        template_id: string;
        nickname: string;
        label_id: string;
        attachments: string;
    };
    order_info: {
        type_id: string;
        order_num: string;
        order_money: string;
        order_conversion_rate: string;
    };
    report_info: {
        request_num: string;
        delivered_num: string;
        click_num: string;
        open_num: string;
        order_num: string;
    };
    webhook_info: {
        email_bounce: string;
        email_invalid: string;
        email_spam: string;
        email_unsubscribe: string;
        is_order: string;
        not_click: string;
        not_open: string;
    };
}
/**
 * 获取发送邮箱计划详情
 * @param params
 * @returns
 */
export const getPlanInfo = (params: PlanInfoParams) => axios.request<Response<PlanInfoRes>>({
    url: '/email/email-marketing/get-plan-info',
    method: 'get',
    params,
});

interface StripoTokenRes {
    token: string;
}
/**
 * 获取stripo token
 * @param params
 * @returns
 */
export const getStripoToken = () => axios.request<Response<StripoTokenRes>>({
    url: '/email/email-marketing/get-stripo-token',
    method: 'get',
});

export interface EmailTemplateListRes {
    id: string;
    oss_url: string;
    title: string;
    category_id: string;
    category_name: string;
    email_content?: string;
    variable_config?: string;
}
interface EmailTemplateListParams {
    page?: number;
    page_size?: number;
    category_id?: string;
    id?: string;
    language?: string;
    search_word?: string;
}
/**
 * 获取邮件模版列表
 * @param params
 * @returns
 */
export const getEmailTemplateList = (params: EmailTemplateListParams) => axios.request<Response<EmailTemplateListRes[]>>({
    url: '/email/email-marketing/get-email-template-list',
    method: 'get',
    params,
});

export interface PlanNameResItem {
    id: string;
    name: string;
}

/**
 * 获取发送邮箱计划列表(名称)
 * @returns
 */
export const getPlanNameList = () => axios.request<Response<PlanNameResItem[]>>({
    url: '/email/email-marketing/get-plan-name-list',
    method: 'get',
    // mock: true,
});

interface CheckPlanNameParams {
    name: string;
    id?: string;
}
/**
 * 检查计划名称重复
 * @returns
 */
export const checkPlanName = (params: CheckPlanNameParams) => axios.request<Response<any>>({
    url: '/email/email-marketing/check-plan-name',
    method: 'get',
    params,
    customTips: true,
    // mock: true,
});

export interface EditItem {
    id: string;
    name: string;
}
export interface EmailTemplateCategoryList {
    count: string;
    category_id: string;
    category_name: string;
    category_key: string;
}
/**
 * 获取邮件模板分类
 * @returns
 */
export const getEmailTemplateCategoryList = (params: { language: string }) => axios.request<Response<EmailTemplateCategoryList[]>>({
    url: '/email/email-marketing/get-email-template-category-list',
    method: 'get',
    params,
    // mock: true,
});

type ActionType = '1' | '2' | '3' | '4' | '5' | '6' | '7';
interface PlanDetailListParams {
    type_id?: string;
    type: string;
    action?: ActionType; // 行为筛选 （1 未打开，2 打开邮件，3 点击链接，4 取消订阅，5 垃圾举报，6 已下单, 7 邮箱有误）
    email_send?: '0' | '1'; // 发送状态（0 未送达， 1 已送达）
    email?: string;
    page: number;
    page_size: number;
}

export interface PlanDetailListItem {
    email: string;
    email_send: string;
    action: ActionType;
    reach_time: string;
    open_time: string;
    click_time: string;
    order_created_time: string;
    order_money: string;
    currency_code: string;
}
/**
 * 获取发送计划明细列表
 * @returns
 */
export const getPlanDetailList = (params: PlanDetailListParams) => axios.request<Response<PlanDetailListItem[]>>({
    url: '/email/email-marketing/get-plan-detail-list',
    method: 'get',
    params,
    // mock: true,
});

/**
 * 获取发送计划明细数量
 * @returns
 */
export const getPlanDetailCount = (params: PlanDetailListParams) => axios.request<Response<{ total: string }>>({
    url: '/email/email-marketing/get-plan-detail-count',
    method: 'get',
    params,
    // mock: true,
});

/**
 * 导出发送计划明细列表
 * @returns
 */
export const exportPlanDetailList = (params: any) => {
    params.lang = ['zh-CN', 'zh-TW'].includes(i18n.locale) ? 'zh-CN' : (i18n.locale || 'zh-CN');
    return exportAction({
        url: 'email/email-marketing/export-plan-detail-list',
        method: 'get',
        params,
    });
};

export interface EmailTemplateData {
    id?: string;
    template_name: string;
    template_content: string;
    email_content: string;
    preview: string;
    category_id: string;
    variable_config: { [key in string]: string };
}

/**
 * 创建邮件模版
 * @param data
 */
export const createEmailTemplate = (data: EmailTemplateData) => axios.request<Response<{ id: string }>>({
    url: '/email/email-marketing/create-template',
    method: 'post',
    data,
});

/**
 * 编辑邮件模版
 * @param data
 */
export const updateEmailTemplate = (data: EmailTemplateData) => axios.request({
    url: '/email/email-marketing/update-template',
    method: 'post',
    data,
});

/**
 * 删除邮件模版
 * @param data
 */
export const delEmailTemplate = (data: { id: string }) => axios.request({
    url: '/email/email-marketing/del-template',
    method: 'post',
    data,
});

export interface ProjectEmailTemplateCategoryData {
    name: string;
    id?: string;
}

/**
 * 创建邮件模板分类
 * @param data
 */
export const updateProjectEmailTemplateCategory = (data: ProjectEmailTemplateCategoryData) => axios.request({
    url: '/email/email-marketing/update-project-email-template-category',
    method: 'post',
    data,
});

/**
 * 邮件模版分类排序接口
 * @param data
 */
export const sortProjectEmailTemplateCategory = (data: { sort_arr: string }) => axios.request({
    url: '/email/email-marketing/sort-project-email-template-category',
    method: 'post',
    data,
});

/**
 * 模板分类列表
 * @param data
 */
export const projectEmailTemplateCategoryList = () => axios.request({
    url: '/email/email-marketing/project-email-template-category-list',
    method: 'post',
});

/**
 * 删除模板分类
 * @param data
 */
export const delProjectEmailTemplateCategory = (data: {id: string}) => axios.request({
    url: '/email/email-marketing/del-project-email-template-category',
    method: 'post',
    data,
});

export interface ProjectEmailTemplateListData {
    category_id?: string;
    page: number;
    page_size: number;
    field_sort: string;
    name?: string;
    id?: string;
}

/**
 * 邮件模板列表接口
 * @param data
 */
export const getProjectEmailTemplateList = (params: ProjectEmailTemplateListData) => axios.request({
    url: '/email/email-marketing/get-project-email-template-list',
    method: 'get',
    params,
});

export interface ProjectEmailTemplateCountData {
    category_id?: string;
    field_sort: string;
    name?: string;
}
/**
 * 邮件模板列表总数
 * @param data
 */
export const getProjectEmailTemplateCount = (params: ProjectEmailTemplateCountData) => axios.request({
    url: '/email/email-marketing/get-project-email-template-count',
    method: 'get',
    params,
});
